import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { MapPin } from "lucide-react";

export interface ShippingAddress {
  name: string;
  address1: string;
  address2: string;
  postal_code: string;
  city: string;
  country_code: string;
  email: string;
  mobile: string;
}

interface ShippingAddressFormProps {
  onSubmit: (address: ShippingAddress) => void;
  onBack: () => void;
  loading?: boolean;
  initialAddress?: Partial<ShippingAddress>;
}

export function ShippingAddressForm({ onSubmit, onBack, loading, initialAddress }: ShippingAddressFormProps) {
  const { t } = useTranslation();
  const [address, setAddress] = useState<ShippingAddress>({
    name: initialAddress?.name || "",
    address1: initialAddress?.address1 || "",
    address2: initialAddress?.address2 || "",
    postal_code: initialAddress?.postal_code || "",
    city: initialAddress?.city || "",
    country_code: "DK",
    email: initialAddress?.email || "",
    mobile: initialAddress?.mobile || "",
  });

  const handleChange = (field: keyof ShippingAddress) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setAddress((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const isValid = address.name && address.address1 && address.postal_code && address.city && address.email;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isValid) onSubmit(address);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="flex items-center gap-2 mb-2">
        <MapPin className="w-5 h-5 text-primary" />
        <h3 className="text-lg font-semibold">{t("recipeBook.shipping.title")}</h3>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="sm:col-span-2">
          <Label htmlFor="shipping-name">{t("recipeBook.shipping.name")} *</Label>
          <Input id="shipping-name" value={address.name} onChange={handleChange("name")} required />
        </div>

        <div className="sm:col-span-2">
          <Label htmlFor="shipping-address1">{t("recipeBook.shipping.address1")} *</Label>
          <Input id="shipping-address1" value={address.address1} onChange={handleChange("address1")} required />
        </div>

        <div className="sm:col-span-2">
          <Label htmlFor="shipping-address2">{t("recipeBook.shipping.address2")}</Label>
          <Input id="shipping-address2" value={address.address2} onChange={handleChange("address2")} />
        </div>

        <div>
          <Label htmlFor="shipping-postal">{t("recipeBook.shipping.postalCode")} *</Label>
          <Input id="shipping-postal" value={address.postal_code} onChange={handleChange("postal_code")} required />
        </div>

        <div>
          <Label htmlFor="shipping-city">{t("recipeBook.shipping.city")} *</Label>
          <Input id="shipping-city" value={address.city} onChange={handleChange("city")} required />
        </div>

        <div>
          <Label htmlFor="shipping-email">{t("recipeBook.shipping.email")} *</Label>
          <Input id="shipping-email" type="email" value={address.email} onChange={handleChange("email")} required />
        </div>

        <div>
          <Label htmlFor="shipping-mobile">{t("recipeBook.shipping.mobile")}</Label>
          <Input id="shipping-mobile" type="tel" value={address.mobile} onChange={handleChange("mobile")} placeholder="+45" />
        </div>
      </div>

      <p className="text-xs text-muted-foreground">{t("recipeBook.shipping.countryNote")}</p>

      <div className="flex gap-3">
        <Button type="button" variant="outline" onClick={onBack}>
          {t("recipeBook.shipping.back")}
        </Button>
        <Button type="submit" disabled={!isValid || loading} className="flex-1">
          {loading ? t("recipeBook.redirecting") : t("recipeBook.shipping.continue")}
        </Button>
      </div>
    </form>
  );
}
